import { TablePaginationConfig, message } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import { useEffect, useState } from 'react';
import invariant from 'tiny-invariant';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { RingTableFilterData } from 'business/production-and-performance/pages/ring-table';
import { RingsTable } from 'business/production-and-performance/pages/ring-table/components/ring-table';
import { useGetFilteredAndPaginatedRingsQuery } from 'business/production-and-performance/pages/ring-table/components/ring-table-container/hooks/use-get-filtered-and-paginated-rings-query';
import { UpdateRingModal } from 'business/production-and-performance/pages/ring-table/components/update-ring-modal';
import { RingTableRow } from 'business/production-and-performance/pages/ring-table/types';
import { useProdAndPerfFilters } from 'business/production-and-performance/providers/prod-and-perf-filters-provider';
import { TableParamsProps } from 'business/providers/table-params';
import { GetRingsQuery } from 'generated/graphql';
import { FilterDataSetter } from 'technical/filter-data';

interface Props {
  tableParams: TableParamsProps;
  onChange?: (
    pagination: TablePaginationConfig,
    _: unknown,
    sorter: SorterResult<RingTableRow> | SorterResult<RingTableRow>[],
  ) => void;
  setFilterData: FilterDataSetter<RingTableFilterData>;
}

export const RingsTableContainer = ({
  tableParams,
  onChange,
  setFilterData,
}: Props) => {
  const { currentConstructionSiteId: constructionSiteId } = useAppContext();
  const filters = useProdAndPerfFilters();
  invariant(constructionSiteId, 'No constructionSite id');

  const [currentRecord, setCurrentRecord] = useState<
    GetRingsQuery['parameter_ring'][number] | null
  >(null);
  const isModalVisible = currentRecord !== null;

  const { data, loading, error } = useGetFilteredAndPaginatedRingsQuery(
    constructionSiteId,
    tableParams,
    filters,
    setFilterData,
  );

  const openEditModal = (id: string | null) => {
    const ring = data?.parameter_ring.find((r) => r.id === id) || null;
    setCurrentRecord(ring);
  };

  const closeEditModal = () => {
    setCurrentRecord(null);
  };

  useEffect(
    function alertUser() {
      if (error) {
        message.error(error.message);
      }
    },
    [error],
  );

  return (
    <>
      <RingsTable
        data={data?.parameter_ring}
        loading={loading}
        pagination={{
          ...tableParams,
          total: data?.parameter_ring_aggregate?.aggregate?.count,
        }}
        onChange={onChange}
        onEditionClick={openEditModal}
      />
      <UpdateRingModal
        isModalVisible={isModalVisible}
        closeModal={closeEditModal}
        ring={currentRecord}
      />
    </>
  );
};
